import { useMutation, useQuery } from '@apollo/client';
import _ from 'lodash';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { Badge, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router';
import { GetLiveOpsRedeemCodesQuery } from '../../../../../__gqltypes__';
import { LoadingLogo } from '../../../devtools/components/Modal';
import RedeemCodeForm from './components/RedeemCodeForm';
import {
  LIVEOPS_REDEEM_CODES_QUERY,
  REDEEM_CODE_FEATURE_CONFIG_QUERY,
  UPDATE_REDEEM_CODE_FEATURE_CONFIG,
} from './graphql';
import {
  compactRewardLabel,
  EditableReward,
  formatEligibilitySummary,
  formatUtcDate,
  getRedeemCodeStatus,
  RedeemCodeItem,
  rewardUnionToInput,
} from './utils';

const baseURL = '/sp3/liveops/redeem-codes';

const REDEEM_LINK_HOST_MAP: Record<string, string> = {
  test: 'test.songpop3.com',
  beta: 'beta.songpop3.com',
  prod: 'songpop3.com',
};

const getRedeemLinkHost = (): string =>
  REDEEM_LINK_HOST_MAP[process.env.REACT_APP_NAMESPACE ?? ''] ?? REDEEM_LINK_HOST_MAP.test;

const buildRedeemLink = (code: string): string => `https://${getRedeemLinkHost()}/redeem/${code}`;

const STATUS_FILTERS = ['All', 'Running', 'Pending', 'Expired', 'Disabled'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const MAX_REWARD_CHIPS = 2;

// Not in generated __gqltypes__ until codegen runs against a deployed backend
type RedeemCodeFeatureConfig = { ios: boolean; android: boolean };
const FEATURE_PLATFORMS = [
  { key: 'ios', label: 'iOS' },
  { key: 'android', label: 'Android' },
] as const;

const rewardsSummary = (redeemCode: RedeemCodeItem): string[] => {
  const rewards = (redeemCode.rewards ?? [])
    .map(rewardUnionToInput)
    .filter((reward): reward is EditableReward => !_.isNil(reward));
  const labels = rewards.slice(0, MAX_REWARD_CHIPS).map(compactRewardLabel);
  if (rewards.length > MAX_REWARD_CHIPS) {
    labels.push(`+${rewards.length - MAX_REWARD_CHIPS}`);
  }
  return labels;
};

function RedeemCodes() {
  const { id: redeemCodeId } = useParams<{ id: string }>();
  const history = useHistory();

  const { data: redeemCodesData, loading: fetchingLoading } = useQuery<GetLiveOpsRedeemCodesQuery>(
    LIVEOPS_REDEEM_CODES_QUERY,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const [redeemCodes, setRedeemCodes] = React.useState<RedeemCodeItem[]>([]);
  const [isCreating, setIsCreating] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('All');
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const [copiedLinkId, setCopiedLinkId] = React.useState<string | null>(null);

  const { data: featureConfigData } = useQuery(REDEEM_CODE_FEATURE_CONFIG_QUERY, { fetchPolicy: 'no-cache' });
  const [updateFeatureConfig, { loading: featureConfigSaving }] = useMutation(UPDATE_REDEEM_CODE_FEATURE_CONFIG);
  const [featureConfig, setFeatureConfig] = React.useState<RedeemCodeFeatureConfig | null>(null);

  useEffect(() => {
    const config = featureConfigData?.liveOps?.redeemCodeFeatureConfig;
    if (config) {
      setFeatureConfig({ ios: config.ios, android: config.android });
    }
  }, [featureConfigData]);

  const handleToggleFeature = (platform: 'ios' | 'android') => {
    if (!featureConfig || featureConfigSaving) {
      return;
    }
    const next = { ...featureConfig, [platform]: !featureConfig[platform] };
    if (
      !next[platform] &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Disable the redeem code feature for ${platform === 'ios' ? 'iOS' : 'Android'} players? ` +
          'The Redeem Code menu will be hidden in the app and every redemption attempt will be rejected.'
      )
    ) {
      return;
    }
    updateFeatureConfig({ variables: { input: next } })
      .then(({ data }) => {
        const saved = data?.updateRedeemCodeFeatureConfig;
        if (saved) {
          setFeatureConfig({ ios: saved.ios, android: saved.android });
        }
      })
      .catch((error) => {
        console.error(error);
        // eslint-disable-next-line no-alert
        alert(`Failed to update the feature config: ${error?.message ?? 'unknown error'}`);
      });
  };

  useEffect(() => {
    if (redeemCodesData && redeemCodesData.liveOps) {
      setRedeemCodes(_.orderBy(redeemCodesData.liveOps.redeemCodes as RedeemCodeItem[], 'created', 'desc'));
    }
  }, [redeemCodesData]);

  // Clean up the raw :id placeholder some navigations leave in the URL
  useEffect(() => {
    if (redeemCodeId === ':id') {
      history.replace(baseURL);
    }
  }, [redeemCodeId, history]);

  const selectedRedeemCode = redeemCodes.find(({ id }) => id === redeemCodeId);

  const filteredRedeemCodes = useMemo(() => {
    const query = search.trim().toUpperCase();
    return redeemCodes.filter((redeemCode) => {
      if (query && !redeemCode.code.includes(query)) {
        return false;
      }
      if (statusFilter !== 'All') {
        return (
          getRedeemCodeStatus(redeemCode.startDate, redeemCode.endDate, redeemCode.enabledIos || redeemCode.enabledAndroid)
            .label === statusFilter
        );
      }
      return true;
    });
  }, [redeemCodes, search, statusFilter]);

  const handleUpsertRedeemCode = (redeemCode: RedeemCodeItem) => {
    const others = redeemCodes.filter(({ id }) => id !== redeemCode.id);
    setRedeemCodes(_.orderBy([...others, redeemCode], 'created', 'desc'));
  };

  const handleSaved = (redeemCode: RedeemCodeItem) => {
    setIsCreating(false);
    handleUpsertRedeemCode(redeemCode);
    history.push(baseURL);
  };

  const handleDeleted = (deletedId: string) => {
    setRedeemCodes((current) => current.filter(({ id }) => id !== deletedId));
    history.push(baseURL);
  };

  const handleBackToList = () => {
    setIsCreating(false);
    history.push(baseURL);
  };

  const handleCopyCode = (redeemCode: RedeemCodeItem, event: React.MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard
      .writeText(redeemCode.code)
      .then(() => {
        setCopiedId(redeemCode.id);
        setTimeout(() => setCopiedId(null), 1500);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleCopyLink = (redeemCode: RedeemCodeItem, event: React.MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard
      .writeText(buildRedeemLink(redeemCode.code))
      .then(() => {
        setCopiedLinkId(redeemCode.id);
        setTimeout(() => setCopiedLinkId(null), 1500);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const showDetail = isCreating || !_.isNil(selectedRedeemCode);

  return (
    <>
      <LoadingLogo show={fetchingLoading} />

      {showDetail ? (
        <div className="mb-5">
          <Button variant="link" className="pl-0 mb-2" onClick={handleBackToList}>
            <i className="fas fa-chevron-left mr-2" />
            Back to list
          </Button>
          <Card>
            <Card.Body>
              {isCreating ? (
                <>
                  <Card.Title>Create Redeem Code</Card.Title>
                  <RedeemCodeForm
                    redeemCode={null}
                    defaultEnabledIos={featureConfig?.ios ?? false}
                    defaultEnabledAndroid={featureConfig?.android ?? false}
                    onSaved={handleSaved}
                    onCancel={handleBackToList}
                  />
                </>
              ) : (
                selectedRedeemCode && (
                  <>
                    <Card.Title className="d-flex align-items-center">
                      <span>{selectedRedeemCode.code}</span>
                      <Badge
                        className="ml-2"
                        pill
                        variant={
                          getRedeemCodeStatus(
                            selectedRedeemCode.startDate,
                            selectedRedeemCode.endDate,
                            selectedRedeemCode.enabledIos || selectedRedeemCode.enabledAndroid
                          ).variant as never
                        }
                      >
                        {
                          getRedeemCodeStatus(
                            selectedRedeemCode.startDate,
                            selectedRedeemCode.endDate,
                            selectedRedeemCode.enabledIos || selectedRedeemCode.enabledAndroid
                          ).label
                        }
                      </Badge>
                    </Card.Title>
                    <div className="text-muted small mb-3">
                      Created: {formatUtcDate(selectedRedeemCode.created)} UTC · Updated:{' '}
                      {formatUtcDate(selectedRedeemCode.updatedAt)} UTC
                    </div>
                    <RedeemCodeForm redeemCode={selectedRedeemCode} onSaved={handleSaved} onDeleted={handleDeleted} />
                  </>
                )
              )}
            </Card.Body>
          </Card>
        </div>
      ) : (
        <div className="mb-5">
          <Card className="mb-3">
            <Card.Body className="py-2 d-flex align-items-center" style={{ gap: '1.5rem' }}>
              <span className="font-weight-bold">
                <i className="fas fa-power-off mr-2" />
                Feature status
              </span>
              {featureConfig ? (
                FEATURE_PLATFORMS.map(({ key, label }) => (
                  <Form.Check
                    key={key}
                    type="switch"
                    id={`redeem-code-feature-${key}`}
                    label={
                      <span>
                        {label}{' '}
                        <Badge pill variant={featureConfig[key] ? 'success' : 'danger'}>
                          {featureConfig[key] ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </span>
                    }
                    checked={featureConfig[key]}
                    disabled={featureConfigSaving}
                    onChange={() => handleToggleFeature(key)}
                  />
                ))
              ) : (
                <Spinner animation="border" size="sm" />
              )}
              {featureConfig && !(featureConfig.ios && featureConfig.android) && (
                <span className="text-danger small ml-auto">
                  The Redeem Code menu is hidden and all redemptions are rejected on disabled platforms.
                </span>
              )}
            </Card.Body>
          </Card>

          <div className="d-flex align-items-center mb-3" style={{ gap: '0.75rem' }}>
            <Form.Control
              type="text"
              placeholder="Search code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 260 }}
            />
            <div>
              {STATUS_FILTERS.map((filter) => (
                <Button
                  key={filter}
                  size="sm"
                  className="rounded-pill mr-1"
                  variant={statusFilter === filter ? 'info' : 'outline-secondary'}
                  onClick={() => setStatusFilter(filter)}
                >
                  {filter}
                </Button>
              ))}
            </div>
            <Button variant="success" className="ml-auto" onClick={() => setIsCreating(true)}>
              <i className="fas fa-plus mr-2" />
              create Redeem Code
            </Button>
          </div>

          <Card>
            <Card.Body className="p-0">
              {filteredRedeemCodes.length > 0 ? (
                <Table hover responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th className="pl-3">Code</th>
                      <th>Status</th>
                      <th>Running date (UTC)</th>
                      <th>Link</th>
                      <th className="text-center">Rewards</th>
                      <th>Matcher</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRedeemCodes.map((redeemCode) => {
                      const status = getRedeemCodeStatus(
                        redeemCode.startDate,
                        redeemCode.endDate,
                        redeemCode.enabledIos || redeemCode.enabledAndroid
                      );
                      return (
                        <tr
                          key={redeemCode.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => history.push(`${baseURL}/${redeemCode.id}`)}
                        >
                          <td className="pl-3 align-middle">
                            <span className="text-monospace font-weight-bold">{redeemCode.code}</span>
                            <Button
                              variant="link"
                              size="sm"
                              className="py-0"
                              title="Copy code"
                              onClick={(e) => handleCopyCode(redeemCode, e)}
                            >
                              <i className={copiedId === redeemCode.id ? 'fas fa-check text-success' : 'far fa-copy'} />
                            </Button>
                          </td>
                          <td className="align-middle">
                            <Badge pill variant={status.variant as never}>
                              {status.label}
                            </Badge>
                          </td>
                          <td className="align-middle text-nowrap">
                            {formatUtcDate(redeemCode.startDate)} →{' '}
                            {redeemCode.endDate ? formatUtcDate(redeemCode.endDate) : '∞'}
                          </td>
                          <td className="align-middle text-nowrap">
                            <div className="d-flex align-items-center" style={{ gap: '0.75rem' }}>
                              <span className="text-muted small text-monospace">
                                {buildRedeemLink(redeemCode.code)}
                              </span>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={(e) => handleCopyLink(redeemCode, e)}
                              >
                                {copiedLinkId === redeemCode.id ? 'Copied!' : 'Copy link'}
                              </Button>
                            </div>
                          </td>
                          <td className="align-middle text-center">
                            {rewardsSummary(redeemCode).map((label) => (
                              <Badge key={label} variant="light" className="border mr-1">
                                {label}
                              </Badge>
                            ))}
                          </td>
                          <td className="align-middle">{formatEligibilitySummary(redeemCode)}</td>
                          <td className="align-middle text-muted">
                            <i className="fas fa-chevron-right" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                !fetchingLoading && (
                  <div className="text-center text-muted py-5">
                    {redeemCodes.length === 0 ? (
                      <>
                        <p className="mb-3">No redeem codes yet.</p>
                        <Button variant="success" onClick={() => setIsCreating(true)}>
                          <i className="fas fa-plus mr-2" />
                          create Redeem Code
                        </Button>
                      </>
                    ) : (
                      <p className="mb-0">No redeem codes match the current search/filter.</p>
                    )}
                  </div>
                )
              )}
            </Card.Body>
          </Card>
        </div>
      )}
    </>
  );
}

export default RedeemCodes;
